import { components } from "@/api-schema";
import { useBrandSearchQuery } from "@/common/api/brand/use-brand-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { BaseFilterProps, Filter, FilterData, FilterPaginationState } from "../_types";
import { FilterComponent } from "../base-filter";

export type QueryBody = components["schemas"]["BrandSearchBody"];

const FILTER_PAGE_SIZE = 50;

// Custom hook to fetch brand data with pagination
function useBrandFilterData(body: QueryBody): { data: FilterData; pagination: FilterPaginationState } {
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: FILTER_PAGE_SIZE,
  });

  const {
    data: brands,
    isPending: isLoading,
    error,
  } = useBrandSearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
      },
      body,
    },
  });

  const options =
    brands?.data
      .map((element) => ({
        id: element.uuid ?? "",
        name: element.name ?? "",
      }))
      .filter((option) => option.id && option.name)
      .filter((option, index, array) => array.findIndex((item) => item.id === option.id) === index) || [];

  const loadedCount = brands?.data?.length ?? 0;
  const total = brands?.meta?.total ?? 0;

  const pagination = createPaginationState(total, loadedCount, isLoading);

  return {
    data: {
      options,
      isLoading,
      error,
    },
    pagination,
  };
}

export class BrandFilter extends Filter<QueryBody, unknown> {
  readonly id: string = "brands";
  readonly labelSingular = "common:Brand";
  readonly labelPlural = "ram:Brands";
  readonly placeholder = "ram:SelectBrands";
  readonly searchPlaceholder = "common:SearchBrands";
  readonly emptyText = "common:NoBrandsFound";

  constructor({ id, body = {} }: { id?: string; body?: Partial<QueryBody> } = {}) {
    super();
    if (id) this.id = id;
    this.body = body as QueryBody;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <BrandFilterComponent filter={this} {...props} />;
  }
}

function BrandFilterComponent({ filter, ...props }: { filter: BrandFilter } & BaseFilterProps) {
  const { data, pagination } = useBrandFilterData(filter.body);

  const filterWithData = Object.create(filter);
  filterWithData.getData = () => data;

  return <FilterComponent filter={filterWithData} pagination={pagination} {...props} />;
}
